import type { CombinedError, Operation } from '@urql/core';
import { mapExchange } from '@urql/core';

import { isTokenValid } from '@shared/auth/token.store';
import { i18n } from '@shared/lib/i18n/config';
import { useToastStore } from '@shared/stores/toast.store';

/** Exported so the toast rules can be unit-tested without urql plumbing. */
export function handleOperationError(error: CombinedError, operation: Operation): void {
  // See SILENT_ERRORS_CONTEXT (../silent-errors.ts).
  if (operation.context['silentErrors'] === true) {
    console.warn(`[GraphQL] ${operation.kind} failed (silent):`, error.message);
    return;
  }

  if (error.graphQLErrors.length > 0) {
    for (const err of error.graphQLErrors) {
      const code = err.extensions['code'] as string | undefined;
      const message = err.message;

      if (code === 'FEATURE_DISABLED') {
        useToastStore.getState().addToast({
          title: i18n.t('featureDisabled', { ns: 'errors' }),
          description: message,
          variant: 'warning',
        });
        continue;
      }

      if (message === 'Forbidden' || message === 'Access denied') {
        // A denial while signed out is not news to the user: the login screen
        // already says what to do, and a warning toast there reads as if the
        // app were broken. Keep the console line -- it is still a real signal
        // for us when a stray authenticated query escapes the auth gate.
        if (isTokenValid()) {
          useToastStore.getState().addToast({
            title: i18n.t('accessDenied', { ns: 'errors' }),
            variant: 'warning',
          });
        }
        console.warn('[GraphQL] Access denied:', message, err);
        continue;
      }

      console.error(`[GraphQL Error]: ${message}`, err);
    }
  }

  if (error.networkError) {
    const isSubscription = operation.kind === 'subscription';

    if (isSubscription) {
      console.warn('[WS Network Error]:', error.networkError.message);
    } else {
      useToastStore.getState().addToast({
        title: i18n.t('serverError', { ns: 'errors' }),
        variant: 'error',
      });
    }
  }
}

export const error = mapExchange({ onError: handleOperationError });
